import {
  CardAdditionalContent,
  CardSizeSelections,
  Price,
  type ProductSelectProps,
} from '@jouzen/ecom-components';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';
import { getPriceAndDiscount } from '@/app/[locale]/product/_components/HardwarePDP/_utils/getPriceAndDiscount';
import type { ModalType } from '@/app/[locale]/product/_components/HardwarePDP/Modals/useModalState';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import SizeSelection from '@/app/[locale]/product/_steps/enums/SizeStep';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import RichText from '@/app/components/RichText';

import type { UseOR4PDPPageReturn } from '../_hooks/useOR4PDPPage';

interface ProductSelectDataProps
  extends Pick<
    UseOR4PDPPageReturn,
    | 'currencyCode'
    | 'monthlyMembership'
    | 'yearlyMembership'
    | 'twoYearWarranty'
    | 'threeYearWarranty'
    | 'onModalOpen'
    | 'onRadioChange'
    | 'onSizeChange'
    | 'order'
    | 'ringSizesData'
  > {
  t: (key: MessageKey) => string;
}

const tValues = ({
  currencyCode,
  amount,
}: {
  currencyCode: string;
  amount: string | number;
}) => ({
  price: function price() {
    return <Price currencyCode={currencyCode} price={amount} />;
  },
});

const getProductSelectData = ({
  currencyCode,
  monthlyMembership,
  yearlyMembership,
  twoYearWarranty,
  threeYearWarranty,
  onModalOpen,
  onRadioChange,
  onSizeChange,
  order,
  ringSizesData,
  t,
}: ProductSelectDataProps): ProductSelectProps[] => {
  const { price: membershipMonthlyPrice } =
    getPriceAndDiscount(monthlyMembership);
  const {
    discountPrice: membershipYearlyDiscount,
    price: membershipYearlyPrice,
  } = getPriceAndDiscount(yearlyMembership);
  const { price: warrantyTwoYearPrice } = getPriceAndDiscount(twoYearWarranty);
  const { price: warrantyThreeYearPrice } =
    getPriceAndDiscount(threeYearWarranty);

  // set initial values for size, membership, and warranty
  const sizeValue = order.size.value;
  const sizeRadioInitialValue =
    sizeValue === SizeSelection.SizingKit.toString()
      ? SizeSelection.SizingKit
      : SizeSelection.ChooseSize;
  const selectedSize =
    sizeValue !== SizeSelection.SizingKit.toString()
      ? { initialSelectedSize: Number(sizeValue) }
      : {};
  const selectedMembership = order.membership.value;
  const selectedWarranty = order.warranty.value;

  const getInfoClick = (section: ModalType) => {
    return () => {
      void window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'info',
        location: section,
      });
      onModalOpen(section);
    };
  };

  if (!ringSizesData) {
    return [];
  }

  return [
    {
      cardSelectorsData: [
        {
          id: SizeSelection.SizingKit,
          title: t('sizing_kit'),
        },
        {
          additionalContent: (
            <CardSizeSelections
              label={t('pdp_step_know_size')}
              message={t('ring_pdp_2025_size_selection_drawer_footnote')}
              onChange={onSizeChange}
              ringSizesData={ringSizesData}
              {...selectedSize}
            />
          ),
          childIds: ringSizesData.map(({ size }) => size.toString()),
          description: t('ring_pdp_2025_size_option_description'),
          id: SizeSelection.ChooseSize,
          showContentOnExpand: true,
          title: t('ring_pdp_2025_size_option_select_size'),
        },
      ],
      description: t('ring_pdp_2025_size_selection_subtitle'),
      infoButtonProps: {
        'aria-label': t('ring_pdp_2025_size_modal_label'),
        onClick: getInfoClick('sizing'),
      },
      initialSelection: sizeRadioInitialValue,
      onChange: (value: string) => {
        const ctaValues: Record<SizeSelection, string> = {
          [SizeSelection.ChooseSize]: 'select your size',
          [SizeSelection.SizingKit]: 'sizing kit',
        };
        void window.ouraAnalytics.track(EventType.ModuleClicked, {
          cta: ctaValues[value as SizeSelection],
          location: 'pdp',
          step: 2,
        });
        onRadioChange(value);
      },
      title: t('pdp_size'),
    },
    {
      cardSelectorsData: [
        {
          description: (
            <>
              {membershipMonthlyPrice ? (
                <RichText
                  messageKey="price_per_mo_2025"
                  tValues={tValues({
                    currencyCode,
                    amount: membershipMonthlyPrice,
                  })}
                />
              ) : null}
            </>
          ),
          id: MembershipSelection.OneMonth,
          title: t('ring_pdp_2025_membership_option_monthly'),
        },
        {
          additionalContent: (
            <CardAdditionalContent
              content={{
                list: [
                  {
                    listItem: t('ring_pdp_2025_membership_annual_bullet_1'),
                    variant: 'body-small',
                  },
                  {
                    listItem: t('ring_pdp_2025_membership_annual_bullet_2'),
                    variant: 'body-small',
                  },
                  {
                    listItem: t('ring_pdp_2025_membership_annual_bullet_3'),
                    variant: 'body-small',
                  },
                  {
                    listItem: t('ring_pdp_2025_membership_annual_bullet_4'),
                    variant: 'body-small',
                  },
                ],
              }}
            />
          ),
          description: (
            <>
              {membershipYearlyPrice ? (
                <Price
                  currencyCode={currencyCode}
                  discount={membershipYearlyDiscount}
                  price={membershipYearlyPrice}
                />
              ) : null}
            </>
          ),
          id: MembershipSelection.OneYear,
          title: t('ring_pdp_2025_membership_option_annual'),
        },
        {
          description: t('ring_pdp_2025_membership_existing_subtitle'),
          id: MembershipSelection.CurrentMember,
          title: t('ring_pdp_2025_membership_option_existing'),
        },
      ],
      description: t('ring_pdp_2025_membership_selection_subtitle'),
      infoButtonProps: {
        'aria-label': t('ring_pdp_2025_membership_modal_label'),
        onClick: getInfoClick('membership'),
      },
      initialSelection: selectedMembership,
      onChange: (value: string) => {
        const ctaValues: Record<MembershipSelection, string> = {
          [MembershipSelection.OneMonth]: 'pay as you go',
          [MembershipSelection.OneYear]: 'buy 12 months up front',
          [MembershipSelection.CurrentMember]: 'i am already an oura member',
        };
        void window.ouraAnalytics.track(EventType.ModuleClicked, {
          cta: ctaValues[value as MembershipSelection],
          location: 'pdp',
          step: 3,
        });
        onRadioChange(value);
      },
      title: t('oura_membership'),
    },
    {
      cardSelectorsData: [
        {
          description: t('ring_pdp_2025_warranty_1_year_subtitle'),
          id: WarrantySelection.OneYear,
          title: t('ring_pdp_2025_warranty_option_1_year'),
        },
        {
          description: (
            <>
              {warrantyTwoYearPrice ? (
                <Price
                  currencyCode={currencyCode}
                  price={warrantyTwoYearPrice}
                />
              ) : null}
            </>
          ),
          id: WarrantySelection.TwoYear,
          title: t('ring_pdp_2025_warranty_option_2_year'),
        },
        {
          description: (
            <>
              {warrantyThreeYearPrice ? (
                <Price
                  currencyCode={currencyCode}
                  price={warrantyThreeYearPrice}
                />
              ) : null}
              <span className="block">
                {t('ring_pdp_2025_warranty_3_year_subtitle')}
              </span>
            </>
          ),
          id: WarrantySelection.ThreeYear,
          title: t('ring_pdp_2025_warranty_option_3_year'),
        },
      ],
      description: t('ring_pdp_2025_warranty_selection_subtitle'),
      infoButtonProps: {
        'aria-label': t('ring_pdp_2025_warranty_modal_label'),
        onClick: getInfoClick('warranty'),
      },
      initialSelection: selectedWarranty,
      onChange: (value: string) => {
        const ctaValues: Record<WarrantySelection, string> = {
          [WarrantySelection.OneYear]: '1-year protection plan',
          [WarrantySelection.TwoYear]: 'upgrade to 2-year',
          [WarrantySelection.ThreeYear]: 'upgrade to 3-year',
        };
        void window.ouraAnalytics.track(EventType.ModuleClicked, {
          cta: ctaValues[value as WarrantySelection],
          location: 'pdp',
          step: 4,
        });
        onRadioChange(value);
      },
      title: t('warranty'),
    },
  ];
};

export default getProductSelectData;
